import React from "react";
import { AbsoluteFill, Img, staticFile, useCurrentFrame, useVideoConfig } from "remotion";
import {
  springFade, springSlideUp, kineticWord, splitWords,
  kenBurnsScale, kenBurnsPan, progressBar,
} from "../utils/animations";
import { crossFadeIn } from "../utils/transitions";

export type CharacterId = "c1" | "c2" | "c3";

export interface CharacterSlideProps {
  characterId?: CharacterId;
  quote: string;
  location?: string;
  sport?: string;
  accentColor?: string;
  brandName?: string;
  backgroundImageUrl?: string;
  showBadge?: boolean;
  stagger?: number;
}

const CHARS: Record<CharacterId, { name: string; role: string; roleColor: string }> = {
  c1: { name: "The Fan",     role: "Young Bettor · 22–28",   roleColor: "#f59e0b" },
  c2: { name: "The Analyst", role: "Sharp · Data-Driven",    roleColor: "#38bdf8" },
  c3: { name: "The Winner",  role: "Confident · Proven",     roleColor: "#22c55e" },
};

export const CharacterSlide: React.FC<CharacterSlideProps> = ({
  characterId = "c2",
  quote,
  location,
  sport,
  accentColor = "#38bdf8",
  brandName = "POWERPICKS",
  backgroundImageUrl,
  showBadge = true,
  stagger = 2.5,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps, durationInFrames } = useVideoConfig();
  const isVertical = height > width;
  const char = CHARS[characterId];

  // Ken Burns on any background image
  const kbScale = kenBurnsScale(frame, durationInFrames, 1.06, "in");
  const kbPanX  = kenBurnsPan(frame, durationInFrames, -18);

  const words = splitWords(quote);
  const sceneOpacity = crossFadeIn(frame, 12);

  return (
    <AbsoluteFill style={{ fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", background: "#020617", opacity: sceneOpacity }}>

      {/* Background image with Ken Burns */}
      {backgroundImageUrl && (
        <div style={{ position: "absolute", inset: 0, transformOrigin: "center", transform: `scale(${kbScale}) translateX(${kbPanX}px)` }}>
          <Img
            src={backgroundImageUrl.startsWith("http") ? backgroundImageUrl : staticFile(backgroundImageUrl)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
      )}

      {/* Gradient overlay — dark bottom half for text legibility */}
      <div style={{ position: "absolute", inset: 0, background: backgroundImageUrl
        ? "linear-gradient(180deg, rgba(2,6,23,0.25) 0%, rgba(2,6,23,0.6) 35%, rgba(2,6,23,0.92) 65%, #020617 90%)"
        : "linear-gradient(160deg, #020617 0%, #060f1e 60%, #020617 100%)"
      }} />

      {/* Subtle grid when no image */}
      {!backgroundImageUrl && (
        <div style={{ position: "absolute", inset: 0, opacity: 0.04,
          backgroundImage: `linear-gradient(${accentColor}44 1px, transparent 1px), linear-gradient(90deg, ${accentColor}44 1px, transparent 1px)`,
          backgroundSize: "60px 60px"
        }} />
      )}

      {/* Top accent bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accentColor, boxShadow: `0 0 20px ${accentColor}88` }} />

      {/* Brand + scene badges */}
      <div style={{ position: "absolute", top: isVertical ? 60 : 40, left: 52, right: 52, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{
          opacity: springFade(frame, fps, 1),
          background: accentColor, color: "#020617",
          padding: "6px 18px", borderRadius: 6,
          fontSize: isVertical ? 26 : 20, fontWeight: 900, letterSpacing: 1, textTransform: "uppercase",
        }}>
          {brandName}
        </div>
        {showBadge && (location || sport) && (
          <div style={{ opacity: springFade(frame, fps, 4), display: "flex", gap: 10 }}>
            {location && (
              <div style={{ background: "rgba(2,6,23,0.85)", border: `1px solid ${accentColor}55`, borderRadius: 20, padding: "6px 16px", fontSize: isVertical ? 20 : 15, color: accentColor, fontWeight: 600 }}>
                {location}
              </div>
            )}
            {sport && (
              <div style={{ background: "rgba(2,6,23,0.85)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 20, padding: "6px 16px", fontSize: isVertical ? 20 : 15, color: "#94a3b8", fontWeight: 600 }}>
                {sport.toUpperCase()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content — anchored to bottom third */}
      <AbsoluteFill style={{ justifyContent: "flex-end", padding: isVertical ? "0 52px 110px" : "0 60px 70px", flexDirection: "column" }}>

        {/* Character name tag */}
        <div style={{
          opacity: springFade(frame, fps, 3),
          transform: `translateY(${springSlideUp(frame, fps, 3, 30)}px)`,
          marginBottom: 20,
          alignSelf: "flex-start",
          background: "rgba(2,6,23,0.88)",
          border: `1px solid ${char.roleColor}44`,
          borderLeft: `3px solid ${char.roleColor}`,
          borderRadius: 8,
          padding: isVertical ? "12px 24px" : "8px 18px",
        }}>
          <div style={{ fontSize: isVertical ? 28 : 20, fontWeight: 800, color: "#f1f5f9", letterSpacing: 0.5 }}>
            {char.name}
          </div>
          <div style={{ fontSize: isVertical ? 20 : 15, color: char.roleColor, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginTop: 2 }}>
            {char.role}
          </div>
        </div>

        {/* Kinetic quote — word-by-word spring reveal */}
        <div style={{ lineHeight: isVertical ? 1.25 : 1.2 }}>
          {words.map((word, i) => {
            const rev = kineticWord(frame, fps, i, stagger);
            return (
              <span
                key={i}
                style={{
                  display: "inline-block",
                  marginRight: isVertical ? 14 : 10,
                  marginBottom: isVertical ? 6 : 4,
                  opacity: rev.opacity,
                  transform: `translateY(${rev.translateY}px) scale(${rev.scale})`,
                  fontSize: Math.min(isVertical ? 76 : 56, width / (isVertical ? 12 : 16)),
                  fontWeight: 900,
                  color: "#f1f5f9",
                  textShadow: "0 2px 30px rgba(0,0,0,0.65)",
                }}
              >
                {word}
              </span>
            );
          })}
        </div>
      </AbsoluteFill>

      {/* Bottom progress bar */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, durationInFrames)}%`, background: accentColor }} />
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
